import type { FC } from 'react';
import { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import type { PapiNetworkbarStrip } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ThemeSurface } from '@wix/da-react-context/pkg/constants';
import SellPromotionCard from '@wix/da-shared-react/pkg/ShopCard/SellPromotionCard';
import Surface from '@wix/da-ds/pkg/Surface';
import { BiData, UserSymbolLearnMoreClickBiEvent } from '@wix/da-bi/pkg/events';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { sanitizeHref } from '@wix/da-url';
import { useMeasuredMobileSwipeCookie } from '../useMeasuredMobileSwipeCookie';
import TitledStrip, { cardWidth } from '../../_partials/TitledStrip';

import s from './RecommendedSellStrip.scss';

interface Props {
  strip: PapiNetworkbarStrip;
  className?: string;
}

const RecommendedSellStrip: FC<Props> = ({ strip, className }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { ref, dimensions } = useMeasuredMobileSwipeCookie();

  const learnMoreLink = t('shopPage.sellYourArt.strips.recommendedSellStrip.headingCta.link');
  const learnMoreBiData = BiData<UserSymbolLearnMoreClickBiEvent>({
    evid: 112,
    link_url: learnMoreLink,
    link_name: 'shop-recommendations',
  });

  return (
    <Surface type={ThemeSurface.SECONDARY} className={c(s['root'], className)}>
      <TitledStrip
        title={t('shopPage.sellYourArt.strips.recommendedSellStrip.heading')}
        description={
          isMobile
            ? t('shopPage.sellYourArt.strips.recommendedSellStrip.description.mobile', {
                postProcess: ['reactPostprocessor'],
                learnMoreLink: (
                  <BiLink
                    className={s['heading-cta']}
                    target="_blank"
                    rel="noreferrer"
                    href={sanitizeHref(learnMoreLink)}
                    biData={learnMoreBiData}
                  >
                    {t('shopPage.sellYourArt.strips.recommendedSellStrip.headingCta.mobile')}
                  </BiLink>
                ),
              })
            : t('shopPage.sellYourArt.strips.recommendedSellStrip.description')
        }
        headingCta={
          !isMobile && (
            <TextButton
              size="medium"
              variant="main"
              target="_blank"
              rel="noreferrer"
              href={sanitizeHref(learnMoreLink)}
              biData={learnMoreBiData}
            >
              {t('shopPage.sellYourArt.strips.recommendedSellStrip.headingCta')}
            </TextButton>
          )
        }
        className={s['content']}
      >
        {strip.deviations &&
          strip.deviations.map(deviation => (
            <div key={deviation.deviationId} className={s['center-for-mobile-slider']} ref={ref}>
              <SellPromotionCard
                deviation={deviation}
                width={isMobile ? (dimensions.width ?? cardWidth) : cardWidth}
              />
            </div>
          ))}
      </TitledStrip>
    </Surface>
  );
};

RecommendedSellStrip.displayName = 'RecommendedSellStrip';

export default RecommendedSellStrip;
